import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/brand/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";

const fmtErr = (d) => {
  if (d == null) return null;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (d?.msg) return d.msg;
  return String(d);
};

export default function PatientLogin() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, login, register } = useAuth();
  const [tab, setTab] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { if (user && user.role === "patient") navigate("/portal", { replace: true }); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      if (tab === "register") {
        if (form.password.length < 8) { setErr(t("portal.errors.passwordShort")); setBusy(false); return; }
        await register(form.name, form.email, form.password);
      } else {
        await login(form.email, form.password);
      }
      navigate("/portal", { replace: true });
    } catch (e2) {
      setErr(fmtErr(e2?.response?.data?.detail) || t("portal.errors.generic"));
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-sand px-5 py-12" data-testid="patient-login">
      <Toaster position="top-center" />
      <div className="w-full max-w-md rounded-3xl bg-warm-ivory p-8 shadow-[0_20px_60px_rgba(26,37,48,0.08)]">
        <div className="mb-6 flex justify-center"><Logo /></div>
        <h1 className="text-center font-serif text-3xl text-deep-ink">{t("portal.title")}</h1>
        <p className="mt-2 text-center text-sm text-deep-ink/60">{t("portal.subtitle")}</p>

        <div className="mt-6 flex rounded-full bg-sand p-1">
          {["login", "register"].map((tb) => (
            <button key={tb} data-testid={`tab-${tb}`} onClick={() => { setTab(tb); setErr(""); }}
              className={`flex-1 rounded-full py-2 text-sm font-medium transition-colors ${tab === tb ? "bg-deep-ink text-warm-ivory" : "text-deep-ink/60"}`}>
              {t(`portal.login.tab${tb === "login" ? "Login" : "Register"}`)}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4">
          {tab === "register" && (
            <div>
              <Label className="mb-1.5 block text-sm text-deep-ink/70">{t("portal.login.name")}</Label>
              <Input data-testid="input-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="bg-sand" />
            </div>
          )}
          <div>
            <Label className="mb-1.5 block text-sm text-deep-ink/70">{t("portal.login.email")}</Label>
            <Input data-testid="input-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="bg-sand" />
          </div>
          <div>
            <Label className="mb-1.5 block text-sm text-deep-ink/70">{t("portal.login.password")}</Label>
            <Input data-testid="input-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className="bg-sand" />
          </div>
          {err && <p data-testid="auth-error" className="text-sm text-terracotta">{err}</p>}
          <Button type="submit" data-testid="auth-submit" disabled={busy} className="w-full gap-2 rounded-full bg-deep-ink py-6 text-warm-ivory hover:bg-champagne-gold hover:text-deep-ink">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {busy ? t("portal.login.loggingIn") : t(`portal.login.${tab === "login" ? "loginBtn" : "registerBtn"}`)}
          </Button>
        </form>
      </div>
    </div>
  );
}
